
import {
  ProjectDossierContact,
  RealEstate,
  UploadFileType,
  TupleOfStringAndUploadFileTypeInput,
  FileExtension
} from '@/__generated__/globalTypes'
import { toLocaleDateTimeString } from '@/utils/dateUtils'
import { SmartQuery } from 'vue-apollo/types/vue-apollo'
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import {
  DeleteRealEstateFileDocument,
  DeleteRealEstateFileMutation,
  DeleteRealEstateFileMutationVariables
} from './gql/__generated__/deleteRealEstateFile.mutation'
import { ProjectRealEstatesDocument } from './gql/__generated__/getProjectRealEstates.query'
import {
  GetRealEstateFilenamesDocument,
  GetRealEstateFilenamesQuery,
  GetRealEstateFilenamesQueryVariables
} from './gql/__generated__/getRealEstateFilenames.query'
import {
  SendDossierDocument,
  SendDossierMutation,
  SendDossierMutationVariables
} from './gql/__generated__/sendDossier.mutation'

const MAX_FILE_SIZE = 100000000 //bytes (~100 MB)
const EMAIL_REGEX = /^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/

interface IDocument {
  isDeletable: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  lastModified?: any | null
  name: string
  path: string
  type: UploadFileType
  fileExtension: FileExtension
}

@Component
export default class RealEstateDocumentsDialog extends Vue {
  @Prop({ default: false }) show!: boolean
  @Prop({ default: null }) realEstate!: RealEstate | null
  @Prop() isSendDossier!: boolean
  @Prop() projectWideDossierEmails!: ProjectDossierContact[]

  filename = ''
  file?: File | null = null
  isLoading = false

  realEstateFiles = Array<IDocument>()
  filesQuery?: SmartQuery<this, GetRealEstateFilenamesQuery, GetRealEstateFilenamesQueryVariables>
  selectedDocuments = Array<IDocument>()
  recipients = ''

  FileExtension = FileExtension

  maxFileNameSize = 120

  selectedResendDossierReasons = []
  // We hardcode the resend reasons in Dutch as the email templates are also in Dutch.
  resendDossierReasons = ['Wijziging tekening', 'Wijziging materialenspecificatie']

  // Collection of allowed file MIME types and file extensions
  acceptedFileTypes = ['application/pdf', 'application/vnd.ms-outlook', '.msg', '.url']
  created() {
    this.filesQuery = this.$apollo.addSmartQuery<GetRealEstateFilenamesQuery, GetRealEstateFilenamesQueryVariables>(
      'GetRealEstateFilenamesQuery',
      {
        query: GetRealEstateFilenamesDocument,
        variables: () => ({
          realEstateId: this.realEstate?.id
        }),
        fetchPolicy: 'network-only',
        update: (data) => data,
        skip: () => !this.realEstate?.id,
        result: (result) => {
          this.realEstateFiles = result.data.realEstate?.files ?? []
        }
      }
    )
  }

  @Watch('show', { immediate: true })
  onVisibilityChange() {
    if (!this.show) {
      this.file = null
      this.filename = ''
      this.recipients = this.buyersEmailAddress + this.projectDefaultEmailAddresses
      this.isLoading = false
      this.realEstateFiles = []
      this.selectedDocuments = []
      this.selectedResendDossierReasons = []
    }
  }

  @Watch('buyersEmailAddress')
  onBuyersEmailAdressChange() {
    if (this.buyersEmailAddress) {
      this.recipients = this.buyersEmailAddress + this.projectDefaultEmailAddresses
    }
  }

  get projectDefaultEmailAddresses() {
    if (!this.projectWideDossierEmails?.length) return ''
    return this.projectWideDossierEmails.map((x) => x.email?.address).join(';   ') + ';'
  }

  get buyersEmailAddress() {
    if (!this.realEstate?.contact?.email.address) return ''
    return this.realEstate.contact.email.address + ';' + '  '
  }

  getFileNameWithoutExtension(filename: string) {
    return filename.includes('.') ? filename.slice(0, filename.lastIndexOf('.')) : filename
  }

  get fileErrors() {
    const result: string[] = []

    if (!this.file) return result

    if (this.file.size > MAX_FILE_SIZE) {
      const maxSizeInMB = Math.floor(MAX_FILE_SIZE / 1024 / 1024)
      const message = this.$t('global.dialogs.upload.errors.maxFileSize')
        .toString()
        .replace('{MAX}', maxSizeInMB.toString())
      result.push(message)
    }

    const mimeType = this.file.type
    // backend will check if the contents of the file actually match the file extension
    const fileExtension = this.file.name.slice(this.file.name.lastIndexOf('.'))

    if (!this.acceptedFileTypes.includes(mimeType || fileExtension)) {
      result.push(this.$t('global.dialogs.upload.errors.invalidType').toString())
    }

    return result
  }

  get filenameErrors() {
    const result = Array<string>()

    if (!this.filename) {
      return result
    }

    if (/[./\\'"<>]/g.test(this.filename)) {
      result.push(this.$t('global.dialogs.upload.errors.invalidName').toString())
    }

    if (this.filename.length > this.maxFileNameSize) {
      const message = this.$t('global.dialogs.upload.errors.maxNameSize')
        .toString()
        .replace('{MAX}', this.maxFileNameSize.toString())
      result.push(message)
    }

    return result
  }

  get recipientErrors() {
    const result = Array<string>()

    if (!this.recipients) {
      return result
    }

    if (this.recipientValues.some((email) => !!email.trim() && !EMAIL_REGEX.test(email))) {
      result.push(this.$t('global.dialogs.upload.errors.invalidEmails').toString())
    }

    return result
  }

  get recipientValues() {
    return this.recipients.split(/[\s;,]+/).filter((x) => !!x)
  }

  get isUploadDataValid() {
    return !!this.file && !!this.filename && !this.fileErrors.length && !this.filenameErrors.length && !this.isLoading
  }

  get isSendDossierDataValid() {
    return (
      !this.isLoading &&
      !!this.selectedDocuments.length &&
      !this.recipientErrors.length &&
      (this.isResendDossier ? !!this.selectedResendDossierReasons.length : true)
    )
  }

  get language() {
    return this.$i18n.locale
  }

  isDocumentSelected(file: IDocument) {
    return this.selectedDocuments.includes(file)
  }

  uploadFile() {
    // validates if there is a file and filename with no errors
    if (!this.isUploadDataValid) return

    this.isLoading = true

    this.$fileApi
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      .uploadFile(`/realEstate/${this.realEstate?.id}/${encodeURIComponent(this.filename)}`, this.file!)
      .then(() => this.filesQuery?.refetch())
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .catch((error: any) => {
        this.$store.dispatch('showErrorDialog', {
          Code: 'E4207',
          Message: error
        })
      })
      .finally(() => {
        this.isLoading = false
        this.file = null
      })
  }

  downloadFile(event: MouseEvent, file: string, name: string) {
    event.stopPropagation()

    return this.$fileApi.downloadFile(
      `/realEstate/${this.realEstate?.id}/${encodeURIComponent(encodeURIComponent(file))}`,
      name
    )
  }

  deleteFile(event: MouseEvent, file: string) {
    event.stopPropagation()

    this.$showConfirmationDialog({
      Callback: () => {
        this.$hideConfirmationDialog()
        this.$apolloMutate<DeleteRealEstateFileMutation, DeleteRealEstateFileMutationVariables>({
          mutation: DeleteRealEstateFileDocument,
          variables: {
            realEstateId: this.realEstate?.id,
            filename: file
          },
          error: 'E4208',
          refetchQueries: [
            {
              query: GetRealEstateFilenamesDocument,
              variables: {
                realEstateId: this.realEstate?.id
              }
            }
          ]
        })
      },
      Code: 'C4023'
    })
  }

  get isResendDossier() {
    return !!this.realEstate?.dossierSentDateUtc
  }

  sendDossier() {
    if (!this.isSendDossierDataValid) return

    if (!this.isResendDossier) {
      return this.callSendDossierMutation()
    }

    // if it's not the first time sending the dossier to this real estate show a warning message
    return this.$showConfirmationDialog({
      Code: 'C4025',
      Callback: () => {
        this.$hideConfirmationDialog()
        this.callSendDossierMutation()
      }
    })
  }

  callSendDossierMutation() {
    this.isLoading = true
    return this.$apolloMutate<SendDossierMutation, SendDossierMutationVariables>({
      mutation: SendDossierDocument,
      variables: {
        realEstateId: this.realEstate?.id,
        fileNames: this.selectedDocuments.map(
          (d) => ({ item1: d.path, item2: d.type } as TupleOfStringAndUploadFileTypeInput)
        ),
        emailRecipients: this.recipientValues,
        resendReasons: this.selectedResendDossierReasons
      },
      refetchQueries: [
        {
          query: ProjectRealEstatesDocument,
          variables: { projectId: this.$route.params?.projectId }
        }
      ],
      error: 'E4209'
    })
      .then(() => this.$emit('close'))
      .finally(() => (this.isLoading = false))
  }

  toLocaleDateTimeString(date?: string) {
    if (!date) return ''
    return toLocaleDateTimeString(date, this.language, false)
  }

  goToPage(type: UploadFileType) {
    switch (type) {
      case UploadFileType.TechnicalDrawing:
        window.open(`/project/${this.realEstate?.project?.id}/real-estate-order/${this.realEstate?.id}`, '_blank')
        break
      case UploadFileType.CustomOffer:
        window.open(`/custom-offer-request/${this.realEstate?.customOfferRequest?.id}`, '_blank')
        break
      case UploadFileType.Drawing3D:
      case UploadFileType.Manual:
      default:
        break
    }
  }

  typeTranslation(uploadFileType: UploadFileType) {
    return this.$t(`global.uploadFileTypes.${uploadFileType}`)
  }
}
